import {WIDGET_KEYS} from "../../../other/constants/WidgetKeys.jsx";
import {
  commentsCounColumn,
  defaultGroupRenderer as groupRenderer,
  displayPermissionNotAllowed, EntityMainNameDefault, getOriginalRecord,
  imagesCountColumn,
  lineThroughRenderer,
  showToast, swivuCheckGridGrants,
  cellAvatarTooltipConfig, showConfirmationDialog, copyTextToClipboard
} from "../../../other/utils/utils.jsx";
import {
  assignViewsState,
  canOpenEditor,
  debounce, getEntityShareLink
} from "../../../other/utils/otherUtils.jsx";
import { duplicateChecklist, exportChecklistItemsApi, importChecklistItemsApi } from "../../../network/network.jsx";
import {STORE_KEYS} from "../../../other/constants/StoreKeys.jsx";
import {
  cellMenuItemCopyContactToClipboard,
  cellMenuItemNudge, cellMenuItemRemoveRow,
  cellMenuItemToggleArchiveRow,
  processCellMenuItems
} from "../../../other/utils/gridCellMenuUtils.jsx";
import {GRANTS} from "../../../other/constants/PrivilegeTypes.jsx";
import { MessageDialog, Store } from "@bryntum/schedulerpro";
import {updateTabTitleWithRecordCount} from "../../../other/utils/editorUtils.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";
import { SwivuStoreArchiveMixin } from "../../stores/mixins/SwivuStoreArchiveMixin.jsx";
import { SwivuStoreAssignedCanViewMixin } from "../../stores/mixins/SwivuStoreAssignedCanViewMixin.jsx";
import {
  DEEP_LINK_URL_KEYS,
  SWIVU_ASSIST_CONFIG
} from "../../../other/constants/OtherKeys.jsx";

const tabTitle = "Checklists";

export const HasChecklistBaseMixin = (base) => (
  class extends base {
    static get $name() {
      return "HasChecklistBaseMixin";
    }

    get swivuDeepLinkSlug() {
      return "checklist";
    }

    static get configurable() {
      return {
        title: tabTitle,
        tab : {
          icon: "fa-regular fa-square-check w-4 h-4"
        },
        items: {
          checklistGrid: {
            type: WIDGET_KEYS.swivuModernGrid,
            swivuGridTitle: "Checklists",
            cls: "checklist-item-grid",
            destroyStore: true,
            height: "100%",
            selectionMode: {
              multiSelect: false,
            },
            preserveScrollOnDatasetChange: true,
            listeners: {
              beforeCellEditStart: "up.onBeforeCellEdit",
              cellDblClick: "up.onDblClickCell",
              cellClick: "up.onClickCell"
            },
            features: {
              cellMenu: {
                items: {
                  cut: false,
                  copy: false,
                  paste: false,
                  removeRow: cellMenuItemRemoveRow({ onItem: "up.onDeleteChecklistRecord", icon: 'fa-regular fa-trash !text-danger' }),
                  archive: cellMenuItemToggleArchiveRow({ onItem: "up.onArchiveChecklistRecord" }),
                  duplicateRecord: {
                    text: "Duplicate Checklist",
                    onItem: 'up.onDuplicateChecklistRecord',
                    icon: "fa-regular fa-clone"
                  },
                  importChecklistItem: {
                    text: "Import Checklist Items",
                    icon: "b-fa-solid b-fa-upload",
                    onItem: "up.onOpenImportChecklistItemsPicker",
                  },
                  exportChecklistItems: {
                    text: "Export Checklist Items",
                    icon: "b-fa-solid b-fa-download",
                    onItem: "up.onExportChecklistItems"
                  },
                  nudgeUsers: cellMenuItemNudge(),
                  copyLink: cellMenuItemCopyContactToClipboard({ onItem: "up.onCopyFileLink", text: "Copy Link" })
                }
              },
              cellTooltip : {
                ...cellAvatarTooltipConfig,
                textContent: true,
              }
            },
            columns: [
              {
                id: "checklist-record-title",
                field: "title",
                type: WIDGET_KEYS.swivuSortColumn,
                text: "Checklist Title",
                width: "25.6em",
                editor: false,
                renderer: ({ value, record }) => EntityMainNameDefault(lineThroughRenderer(value, record.archived)),
              },
              {
                id: "can-edit",
                field: "canEdit",
                text: "Can Edit",
                type: WIDGET_KEYS.swivuNoteChangeRoleColumn,
                finalizeCellEdit: "up.onFinalizeCanEdit",
                groupable: false,
                sortable: false,
              },
              {
                id: "can-view",
                field: "canView",
                text: "Can View",
                type: WIDGET_KEYS.swivuNoteChangeRoleColumn,
                finalizeCellEdit: "up.onFinalizeCanView",
                groupable: false,
                sortable: false,
              },
              {
                id: "checklist-record-assigned-users",
                type: WIDGET_KEYS.assignToColumn,
                swivuListTitle: "Checklist Assigned To",
              },
              imagesCountColumn("imageItemsCount"),
              commentsCounColumn("commentItemsCount"),
              {
                id: "checked",
                field: "uncheckedItemsCount",
                text: "Checked",
                editor: false,
                flex: 1,
                renderer: ({ value, record }) =>  {
                  if (record.isGroupHeader) return;
                  const style = {  color: !value ? "#0a88e9" : "#dc7f00" };
                  return {
                    tag: "span",
                    text: value ? `${value} Left` : "All Checked",
                    style
                  }
                },
                groupRenderer({ groupRowFor: value }) {
                  const style = {  color: !value ? "#0a88e9" : "#dc7f00" };
                  return {
                    text: value ? `${value} Left` : "All Checked",
                    style
                  }
                }
              },
              { id: "nudged", text: "Nudged", editor: false , field: "nudgeDate", groupRenderer },
              { id: "aging", field: "aging", text: "Age", type: "agingColumn", editor: false, width: "13.8em" },
            ],
            tbar: {
              items: {
                addButton: {
                  onClick: "up.onAddChecklistRecord",
                  cls: 'swivu-modern-button b-blue b-raised',
                  text: "Add Checklist",
                  icon: "b-fa b-fa-plus b-button-icon",
                },
                importChecklistItemsField: {
                  type: "filefield",
                  accept: "text/plain",
                  hidden: true,
                  onChange: "up.onImportChecklistItem"
                }
              }
            },
          }
        },
        swivuAssistance: SWIVU_ASSIST_CONFIG.checklist
      }
    }


    onDuplicateChecklistRecord() {
      const { checklistGrid } = this.widgetMap;
      const selectedRecord = checklistGrid.selectedRecord;

      if (selectedRecord) {
        const checklistPhantomRecord = this.project.checklistStore.createRecord({
          [this.recordType]: this.record.id,
          title: `Copy of ${selectedRecord.title}`,
          archived: selectedRecord.archived,
        });
        checklistGrid.store.add(checklistPhantomRecord);

        const detacher = checklistGrid.store.on("idChange", () => {
          duplicateChecklist(checklistPhantomRecord.id, {
            board: selectedRecord.boardId,
            archived: selectedRecord.archived,
            duplicate_id: selectedRecord.id
          }).then(({ data }) => {
            this.project.applyChangeset({
              [STORE_KEYS.checklistStore]: { added: data[STORE_KEYS.checklistStore].rows },
              [STORE_KEYS.checklistItemStore]: { added: data[STORE_KEYS.checklistItemStore].rows }
            });
          });
          detacher();
        });
      }
    }

    openChecklistItemEditor(record) {
      this.editorHandler.openEditor(EDITOR_KEYS.checklistItemEditor, record, {
        checklist: record,
      });
    }

    onDblClickCell({column, record}) {
      if (column.id === 'checklist-record-title' && !record.isGroupHeader) {
        this.openChecklist(record);
      }
    }

    loadGridData() {
      const { checklistGrid } = this.widgetMap;
      checklistGrid.store = new (SwivuStoreArchiveMixin(SwivuStoreAssignedCanViewMixin(Store, this.project.boardUsersStore)))({
        masterStore: this.project.checklistStore,
        chainedFilterFn: ((checklist) => checklist[this.recordType] === this.record.id)
      });
    }

    // Todo: rowReorder is broken find a reliable way to provide `project` instance to the place from where error is thrown
    // Bug: Grid will not reflect new records added via websocket
    loadRecord(record, context, otherParams) {
       /* Calling super.loadRecord to update reference of context if it has changed; Because else wise if we try to use objects from context like editor handler there reference becomes old and causes issues, therefore keeping super.loadRecord before the firstShow check */
      super.loadRecord(record, context);
      if (otherParams.isFirstShow) {
        this.context = context;
        const { checklistGrid } = this.widgetMap;

        // Removing record deletion on delete key because it could trigger record deletion for those users who are not allowed to delete records
        checklistGrid.keyMap["Delete"].handler = () => {
        }
        checklistGrid.columns.getById("can-edit").onLoad(context.project, { context });
        checklistGrid.columns.getById("can-view").onLoad(context.project, { context });
        checklistGrid.columns.getById("checklist-record-assigned-users").onLoad(context.project, { editorHandler: context.editorHandler });
        this.loadGridData();
        checklistGrid.stateId = this.stateId;
        checklistGrid.store.applyState({});
        assignViewsState([checklistGrid], context.stateProvider);
        checklistGrid.features.cellMenu.processItems = (e) => {
          processCellMenuItems({
            grid: checklistGrid,
            user: context.user,
            project: context.project,
          })(e);
          e.items.removeRow.hidden = false;
        };

        swivuCheckGridGrants(checklistGrid, context.user);

        this.checklistGridUpdateListner = updateTabTitleWithRecordCount(this, checklistGrid.store, tabTitle);

        if (otherParams.checklist) {
          setTimeout(() => {
            this.openChecklist(otherParams.checklist)
          }, 200)
        }
        this.debouncedCellClick =  debounce(({ column, record: clickedRecord, event }) => {
          const originalRecord = getOriginalRecord(clickedRecord)
          if (canOpenEditor(column, originalRecord) && event.detail === 1 && column.isAssignToColumn && context.user.can(originalRecord.canEdit)) {
            context.editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
              entityRecord: originalRecord,
              event,
              column
            }, { by: "recordType", useExisting: true });
          }
        }, 200);
      }
    }

    onCopyFileLink({ record }) {
      const {linkOnly} = getEntityShareLink({
        record: record.entity,
        context: this,
        editorTabs: "checklist",
        entityType: this.deepLinkUrlKey,
        otherKeys: { [DEEP_LINK_URL_KEYS.checklist]: record.id },
        entityDetail: { name: record.title }
      })
      copyTextToClipboard(linkOnly, `Link to ${record.title} copied to clipboard successfully!`)
    }


    displayPermissionNotAllowed(keyMsg) {
      const currentRole = this.boardManager.selectedBoard.record.roleNames;
      MessageDialog.alert({ title: "Permission Denied!", message: `Your role is "${currentRole}", and You don't have permission to ${keyMsg} this record.` });
    }

    openChecklist(record) {
      const currentBoardUser = this.project.boardUsersStore.find((boardUser) => boardUser.userId === this.user.id);
      if (record.assignedBoardUsers.includes(currentBoardUser.id) || this.user.can(record.canView)) {
        this.openChecklistItemEditor(record)
      } else {
        this.displayPermissionNotAllowed("view");
      }
    }

    onAddChecklistRecord() {
      if (!this.record.isPhantom) {
        const order = this.project.checklistStore.last?.order + 1 || 0;
        const checklistRecord = this.project.checklistStore.createRecord({
          title: "",
          [this.recordType]: this.record.id,
          archived: this.record.archived,
          canEdit: GRANTS.grant_five,
          canView: GRANTS.grant_six,
          order
        });
        checklistRecord.project = this.project;
        this.openChecklist(checklistRecord)
      }
    }

    onArchiveChecklistRecord() {
      const { checklistGrid } = this.widgetMap;
      if (checklistGrid.selectedRecord) {
        if (this.user.can(checklistGrid.selectedRecord.canEdit)) {
          checklistGrid.selectedRecord.archived = !checklistGrid.selectedRecord.archived
        } else {
          displayPermissionNotAllowed("archive", this.boardManager.selectedBoard.record.roleNames)
        }
      }
    }

    onDeleteChecklistRecord() {
      const { checklistGrid } = this.widgetMap;
      if (checklistGrid.selectedRecord) {
        const selectedIds = checklistGrid.selectedRecords.map((r) => r.id);
        if (this.user.can(checklistGrid.selectedRecord.canEdit)) {
          showConfirmationDialog({
            title: `Delete Checklist`,
            message: `<div>Are you sure you want to delete the checklist "${checklistGrid.selectedRecord?.title}"?</div>`,
            cancelBtnText: "Cancel",
            confirmBtnText: "Delete",
            isCloseByHeader: true,
          }).then((isDelete) => {
            if (isDelete) {
              checklistGrid.store.remove(selectedIds);
              this.project.checklistStore.remove(selectedIds);
            }
          });
        } else {
          displayPermissionNotAllowed("delete", this.boardManager.selectedBoard.record.roleNames)
        }
      }
    }

    async onExportChecklistItems({ record }) {
      try {
        await exportChecklistItemsApi(record.id);
        showToast({ msg: "Data has been exported successfully.", type: "success" })
      } catch (e) {
        console.log(e)
      }
    }

    onOpenImportChecklistItemsPicker() {
      const { checklistGrid, importChecklistItemsField } = this.widgetMap;
      if (this.user.can(checklistGrid.selectedRecord.canEdit)) {
        importChecklistItemsField.input.click();
      }
    }

    onImportChecklistItem(e) {
      const file = e.source.files?.[0];
      if (file) {
        if (file && file.type === 'text/plain') {
          const { checklistGrid } = this.widgetMap;
          const selectedRecord = checklistGrid.selectedRecord
          const fileReader = new FileReader();
          fileReader.onload = async ({ target: { result: checklistItemsData } }) => {
            try {
              const { data } = await importChecklistItemsApi(selectedRecord.id, checklistItemsData, this.mask("Importing 0%"));
              this.project.applyChangeset({
                [STORE_KEYS.checklistItemStore]: { added: data[STORE_KEYS.checklistItemStore].rows }
              });
            } catch (e) {
              console.log(e);
              MessageDialog.alert({
                title: "Error",
                message: e.message || "Internal Server Error. Please contact support for further assistance."
              });
            } finally {
              e.source.clear();
              e.source.value = null; // clearing this will call this import function again, so make sure to check for file length
              this.unmask();
            }
          };
          fileReader.readAsText(file);
        } else {
          MessageDialog.alert({
            title: "Error",
            message: `Unsupported File Type. Please upload files with extensions: .txt}`
          });
        }
      }
    }

    onFinalizeCanView({ value, record }) {
      if (value < record.canEdit) {
        return "View permission cannot be higher than Edit permission"
      }
      return true;
    }

    onFinalizeCanEdit({ value, record }) {
      if (value > record.canView) {
        record.canView = value;
      }
      return true;
    }

    onBeforeCellEdit({ editorContext: {record} }) {
      const canEdit = this.user.can(record.canEdit);
      if (!canEdit) {
        displayPermissionNotAllowed("update", this.boardManager.selectedBoard.record.roleNames)
      }
      return canEdit;
    }


    getEditableField() {
      return false;
    }

    onSwivuEditorHide() {
      if (this.checklistGridUpdateListner) {
        this.checklistGridUpdateListner();
      }
    }

    onClickCell(event) {
      this.debouncedCellClick(event)
    }
  }
)
